const FIELD_ALIASES = {
  title: ["title", "event_title", "event_name", "program_name", "festival_title"],
  host_name: ["host_name", "host", "family_name", "host_family_name", "organizer", "mandal_name"],
  event_date: ["event_date", "date", "wedding_date", "ceremony_date", "program_date"],
  event_time: ["event_time", "time", "wedding_time", "ceremony_time", "puja_time", "aarti_time"],
  location: ["location", "venue", "venue_name", "venue_address", "full_address", "address", "city_state"],
  bride_name: ["bride_name", "bride", "partner_1", "partner1"],
  groom_name: ["groom_name", "groom", "partner_2", "partner2"],
  birthday_person: ["birthday_person", "celebrant", "celebrant_name"],
  age_celebrated: ["age_celebrated", "age", "turning"],
  parent_names: ["parent_names", "parents"],
  graduate_name: ["graduate_name", "graduate"],
  school_name: ["school_name", "university", "college"],
  company_name: ["company_name", "company"],
  rsvp_contact: ["rsvp_contact", "rsvp", "contact"],
};

const GENERIC_PLACEHOLDER_VALUES = new Set([
  "bride name",
  "groom name",
  "partner 1 name",
  "partner 2 name",
  "host name",
  "host or family name",
  "venue name",
  "full address",
  "city, state",
  "event date",
  "wedding date",
  "wedding time",
  "event time",
  "event location",
  "venue details",
  "rsvp contact",
  "celebrant name",
  "graduate name",
  "company name",
  "program or festival name",
  "festival or program title",
]);

const MONTH_INDEX_BY_NAME = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

function normalizeKey(value = "") {
  return `${value || ""}`
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function normalizeText(value = "") {
  return `${value || ""}`
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeAddressText(value = "") {
  return `${value || ""}`
    .split(/\r?\n+/)
    .map((line) => normalizeText(line))
    .filter(Boolean)
    .join(", ");
}

function parseJsonSafely(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function isUsableValue(value) {
  const normalized = normalizeText(value);
  if (!normalized) return false;
  if (normalized.length < 2) return false;
  if (GENERIC_PLACEHOLDER_VALUES.has(normalized.toLowerCase())) return false;
  return true;
}

function findFieldKey(rawKey) {
  const normalized = normalizeKey(rawKey);
  if (!normalized) return "";

  if (FIELD_ALIASES[normalized]) {
    return normalized;
  }

  return (
    Object.entries(FIELD_ALIASES).find(([, aliases]) =>
      aliases.some((alias) => normalized === normalizeKey(alias) || normalized.includes(normalizeKey(alias)))
    )?.[0] || ""
  );
}

function getTextValue(element) {
  if (!element || element.type !== "text") return "";
  return normalizeText(element.text || element.content || element.placeholder || "");
}

function getRawTextValue(element) {
  if (!element || element.type !== "text") return "";
  return `${element.text || element.content || element.placeholder || ""}`.trim();
}

function getCandidateKeys(element) {
  const custom = element?.custom || {};
  return [
    custom.field_key,
    custom.maps_to_event_field,
    custom.placeholder_key,
    custom.role,
    element.placeholder,
    element.name,
    element.id,
  ].filter(Boolean);
}

function selectPages(json, options = {}) {
  const pages = Array.isArray(json?.pages) ? json.pages : [];
  const pageId = options.pageId || options.page_id;
  const pageIndex =
    options.pageIndex ?? options.page_index ?? (Number.isInteger(options.page) ? options.page : undefined);

  if (pageId) {
    const matchedById = pages.filter((page) => `${page?.id || ""}` === `${pageId}`);
    if (matchedById.length) return matchedById;
  }

  if (Number.isInteger(pageIndex) && pages[pageIndex]) {
    return [pages[pageIndex]];
  }

  return pages;
}

function collectTextElements(json, options = {}) {
  const pages = selectPages(json, options);
  return pages.flatMap((page) => (Array.isArray(page?.children) ? page.children : []))
    .filter((element) => element?.type === "text");
}

function normalizeDate(value) {
  const text = normalizeText(value)
    .replace(/\b(\d{1,2})(st|nd|rd|th)\b/gi, "$1")
    .replace(/^[a-z\s]+:\s*/i, "");
  if (!text) return "";

  const isoDate = text.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (isoDate) return isoDate[0];

  const numericDate = text.match(/\b(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})\b/);
  if (numericDate) {
    const [, first, second, yearRaw] = numericDate;
    const year = yearRaw.length === 2 ? `20${yearRaw}` : yearRaw;
    const firstNumber = Number(first);
    const secondNumber = Number(second);
    const isMonthFirst = secondNumber > 12;
    const month = String(isMonthFirst ? firstNumber : secondNumber).padStart(2, "0");
    const day = String(isMonthFirst ? secondNumber : firstNumber).padStart(2, "0");
    if (Number(month) >= 1 && Number(month) <= 12 && Number(day) >= 1 && Number(day) <= 31) {
      return `${year}-${month}-${day}`;
    }
  }

  const dayMonthYear = text.match(
    /\b(\d{1,2})\s+(?:of\s+)?([a-z]{3,9})\.?,?\s+(\d{4})\b/i
  );
  if (dayMonthYear) {
    const [, dayRaw, monthRaw, year] = dayMonthYear;
    const monthNumber = MONTH_INDEX_BY_NAME[monthRaw.toLowerCase()];
    if (monthNumber) {
      return `${year}-${String(monthNumber).padStart(2, "0")}-${dayRaw.padStart(2, "0")}`;
    }
  }

  const monthDayYear = text.match(/\b([a-z]{3,9})\.?\s+(\d{1,2}),?\s+(\d{4})\b/i);
  if (monthDayYear) {
    const [, monthRaw, dayRaw, year] = monthDayYear;
    const monthNumber = MONTH_INDEX_BY_NAME[monthRaw.toLowerCase()];
    if (monthNumber) {
      return `${year}-${String(monthNumber).padStart(2, "0")}-${dayRaw.padStart(2, "0")}`;
    }
  }

  const currentYear = new Date().getFullYear();
  const dayMonthNoYear = text.match(/\b(\d{1,2})\s+(?:of\s+)?([a-z]{3,9})\.?\b/i);
  if (dayMonthNoYear) {
    const [, dayRaw, monthRaw] = dayMonthNoYear;
    const monthNumber = MONTH_INDEX_BY_NAME[monthRaw.toLowerCase()];
    if (monthNumber) {
      const candidate = new Date(currentYear, monthNumber - 1, Number(dayRaw));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const year = candidate < today ? currentYear + 1 : currentYear;
      return `${year}-${String(monthNumber).padStart(2, "0")}-${dayRaw.padStart(2, "0")}`;
    }
  }

  const monthDayNoYear = text.match(/\b([a-z]{3,9})\.?\s+(\d{1,2})\b/i);
  if (monthDayNoYear) {
    const [, monthRaw, dayRaw] = monthDayNoYear;
    const monthNumber = MONTH_INDEX_BY_NAME[monthRaw.toLowerCase()];
    if (monthNumber) {
      const candidate = new Date(currentYear, monthNumber - 1, Number(dayRaw));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const year = candidate < today ? currentYear + 1 : currentYear;
      return `${year}-${String(monthNumber).padStart(2, "0")}-${dayRaw.padStart(2, "0")}`;
    }
  }

  const parsed = Date.parse(text);
  if (!Number.isNaN(parsed)) {
    return new Date(parsed).toISOString().slice(0, 10);
  }

  return "";
}

function normalizeTime(value) {
  const text = normalizeText(value);
  const time = text.match(/\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/i);
  if (!time) return "";

  let hours = Number(time[1]);
  const minutes = time[2] || "00";
  const meridiem = time[3]?.toLowerCase();

  if (meridiem === "pm" && hours < 12) hours += 12;
  if (meridiem === "am" && hours === 12) hours = 0;
  if (hours > 23) return "";

  return `${String(hours).padStart(2, "0")}:${minutes}`;
}

function normalizeFieldValue(fieldKey, value) {
  if (fieldKey === "event_date") return normalizeDate(value);
  if (fieldKey === "event_time") return normalizeTime(value);
  if (fieldKey === "location") return normalizeAddressText(value);
  return normalizeText(value);
}

function inferFieldKeyFromValue(value) {
  if (normalizeDate(value)) return "event_date";
  return "";
}

export function extractEventDetailsFromPolotno(htmltext, options = {}) {
  const json = parseJsonSafely(htmltext);
  if (!json) return {};

  return collectTextElements(json, options).reduce((details, element) => {
    const rawValue = getTextValue(element);
    const rawSourceValue = getRawTextValue(element);
    if (!isUsableValue(rawValue)) return details;

    const inferredDate = normalizeDate(rawValue);
    const detailsWithInferredDate =
      inferredDate && !details.event_date ? { ...details, event_date: inferredDate } : details;

    const fieldKey = getCandidateKeys(element).map(findFieldKey).find(Boolean) || inferFieldKeyFromValue(rawValue);
    if (!fieldKey || (fieldKey !== "location" && detailsWithInferredDate[fieldKey])) {
      return detailsWithInferredDate;
    }

    const value = normalizeFieldValue(fieldKey, rawValue);
    if (!isUsableValue(value)) return detailsWithInferredDate;

    if (fieldKey === "location") {
      const addressValue = normalizeFieldValue(fieldKey, rawSourceValue);
      if (!isUsableValue(addressValue)) return detailsWithInferredDate;
      if (!detailsWithInferredDate.location) {
        return { ...detailsWithInferredDate, location: addressValue };
      }
      if (detailsWithInferredDate.location.toLowerCase().includes(addressValue.toLowerCase())) {
        return detailsWithInferredDate;
      }
      return { ...detailsWithInferredDate, location: `${detailsWithInferredDate.location}, ${addressValue}` };
    }

    return { ...detailsWithInferredDate, [fieldKey]: value };
  }, {});
}

export function detectPolotnoEventPlaceholders(htmltext, options = {}) {
  const json = parseJsonSafely(htmltext);
  if (!json) return [];

  return collectTextElements(json, options).reduce((placeholders, element) => {
    const rawValue = getTextValue(element);
    const fieldKey = getCandidateKeys(element).map(findFieldKey).find(Boolean);

    if (!fieldKey || !rawValue || !GENERIC_PLACEHOLDER_VALUES.has(rawValue.toLowerCase())) {
      return placeholders;
    }

    if (placeholders.some((item) => item.fieldKey === fieldKey)) {
      return placeholders;
    }

    return [...placeholders, { fieldKey, label: rawValue }];
  }, []);
}
